import { add } from '@cube3/common/utils/add';
import { Field, Validator, Value, Error } from '../../types/GenericFormTypes';
import { getFieldValue } from '../../utils/getFieldValue';
import { validate } from '../../utils/validate';

/** Form reducer actions */
export const genericFormActions = {
  CHANGE_VALUE: 'CHANGE_VALUE',
  INITIALIZE: 'INITIALIZE'
} as const;

export interface GenericFormState {
  fields: readonly Field[];
  values: Value[];
  valid: ValidityState;
  errors: Error[];
  warnings: Error[];
  touched: string[];
}

export enum ValidityState {
  Valid = 1,
  Invalid = 0,
  Warning = 2,
  Validating = 3
}

export const defaultState: GenericFormState = {
  fields: [],
  values: [],
  errors: [],
  warnings: [],
  touched: [],
  valid: ValidityState.Valid
};

type ChangeActionValue = {
  id?: string;
  value: unknown;
};

export const makeReducer =
  (validators: Validator[]) =>
  (
    state: GenericFormState,
    action: {
      type: keyof typeof genericFormActions;
      payload: ChangeActionValue | GenericFormState;
    }
  ): GenericFormState => {
    switch (action.type) {
      case genericFormActions.CHANGE_VALUE: {
        const { id: field, value } = action.payload as ChangeActionValue;

        const newValues = state.values
          .filter((v) => v.id !== field)
          .concat({
            id: field,
            value: value
          } as Value);

        // Validate form whenever values change
        const { errors, warnings } = validate(
          validators,
          newValues,
          state.fields as Field[]
        );

        return {
          ...state,
          values: newValues,
          errors: errors,
          warnings: warnings,
          touched: add(state.touched, field) as string[],
          valid: getValidityState(errors, warnings)
        };
      }
      case genericFormActions.INITIALIZE:
        return action.payload as GenericFormState;

      default:
        return state;
    }
  };

export const getValidityState = (errors, warnings) =>
  errors?.length
    ? ValidityState.Invalid
    : warnings?.length
    ? ValidityState.Warning
    : ValidityState.Valid;
